'use client';

import cypressEnv from 'utils/testing/cypress-env';

import { TF_DEV_PORT, OP_DEV_PORT } from './devPorts';
// import { GITHUB_RUN_NUMBER, MOCKED } from './constants';
import {
  defaultConfig,
  devOps,
  devOpsCn,
  devTF,
  devTFCn,
  drOps,
  drTF,
  qaTF,
  qaOps,
  localOps,
  localTF,
  prodOps,
  prodOpsCn,
  prodTF,
  prodTFCn,
} from './envByHost';

export interface IEnvironment {
  ENV: 'dev' | 'prod' | 'elive';
  GQL_URL: string;
  MEDIA_URL: string;
  TF_SVCGATEWAY_URL: string;
  TEACHER_CHATBOT_URL: string;
  CATALYST_PLAYER_URL: string;
  CONTENT_PREVIEW_URL: string;
  AVAILABILITY_INTERVAL: number;
  BOOKING_INTERVAL: number;
  USER_INTERVAL: number;
  ENTRY_STATUS_INTERVAL: number;
  ACR_TODO_INTERVAL: number;
  LESSON_DEFINITION_INTERVAL: number;
  UNCLAIMED_LESSONS_INTERVAL: number;
  DISCONNECT_AFTER: number;
  HELP_CENTER_URL: string;
  TECH_CHECK_URL_TEMPLATE: string;
  HC_PREVIEW_URL: string;
  LOGIN: {
    SIGNIN_SIGNUP_USER_FLOW: string;
    RESET_USER_FLOW: string;
    AUTHORITY_BASE_URL: string;
    CLIENT_ID: string;
    TOKEN_SCOPE: string;
  };
  MOCKED?: boolean;
  DATADOG?: {
    APPLICATION_ID: string;
    CLIENT_TOKEN: string;
    SERVICE_NAME: string;
    VIRTUAL_ENV: string;
  };
  API_KEYS: {
    amplitude?: string;
  };
  EVC_TUNNELS: {
    US_NV: string;
    CN_SH: string;
    JP_TYO: string;
  };
  VERSION?: string; // we use build number as a version until find need/way to do semver
  STORYBLOK?: {
    TOKEN: string; // public delivery token only
    VERSION: 'draft' | 'published';
    BASE_URL?: string; // override base if needed
    SPACE_ID?: string; // optional
  };
  /**
   * Pre-authored ACCI lesson for the "curriculum aligned" assignment branch
   * (EFEKTA-19327, temporary). Per-ACCI-deployment: dev/qa share dev ACCI.
   * When omitted the curriculum topic source is not offered.
   */
  CURRICULUM_ACCI_LESSON_ID?: string;
  /**
   * Which topic list the curriculum branch offers (EFEKTA-20300, temporary).
   * Paraná BNCC skills when omitted.
   */
  CURRICULUM_TOPIC_LIST?: 'parana' | 'sao-paulo';
  /**
   * Efekta Insights ingestion (EFEKTA-18848). Absence of this block is the
   * feature flag: only `dev-tf` sets it today, so `logEvent`'s dual-write is a
   * no-op everywhere else (prod included) until a follow-up ticket turns it on
   * there. WRITE_KEY ships to every browser by design — it's a bar-raiser
   * against casual abuse, not a secret (see efekta-vibe-insights/lib/write-key.ts).
   */
  INSIGHTS?: {
    INGEST_URL: string;
    WRITE_KEY: string;
  };
}

const isCypressComponentTesting = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window?.Cypress?.testingType === 'component';
};

export const getEnvByHostname = () => {
  // Handle server-side rendering in Next.js
  if (typeof window === 'undefined') {
    return defaultConfig;
  }

  if (isCypressComponentTesting()) {
    return cypressEnv;
  }

  const { hostname, port } = window.location;
  console.debug('loading env configuration for hostname', hostname); // eslint-disable-line no-console
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (port === TF_DEV_PORT) {
      return localTF;
    }
    if (port === OP_DEV_PORT) {
      return localOps;
    }
  }

  switch (hostname) {
    case 'ef.studio':
    case 'teach.ef.studio':
    case 'login.teach.efekta.com':
    case 'teach.efekta.com':
    case 'showcase.teach.ef.studio':
      return prodTF;
    case 'ops-portal.efekta.io':
    case 'ops-portal.ef.studio':
      return prodOps;
    case 'dev.efekta.io':
    case 'dev.ef.studio':
    case 'teach.dev.efekta.io':
    case 'teach.dev.ef.studio':
    case 'showcase.teach.dev.ef.studio':
      return devTF;
    case 'feature-test-teach.ef.studio':
    case 'teach-qa.dev.ef.studio':
      return qaTF;
    case 'teach-dr.dev.ef.studio':
      return drTF;
    case 'ops-portal-dr.dev.ef.studio':
      return drOps;
    case 'dev-ops-portal.efekta.io':
    case 'ops-portal.dev.efekta.io':
    case 'ops-portal.dev.ef.studio':
      return devOps;
    case 'feature-test-ops-portal.ef.studio':
    case 'ops-portal-qa.dev.ef.studio':
      return qaOps;
    case 'teach.dev.ef.studio.ef.com.cn':
      return devTFCn;
    case 'ops-portal.dev.ef.studio.ef.com.cn':
      return devOpsCn;
    case 'teach.ef.studio.ef.com.cn':
      return prodTFCn;
    case 'ops-portal.ef.studio.ef.com.cn':
      return prodOpsCn;
    default:
      return defaultConfig;
  }
};

const env: IEnvironment = getEnvByHostname();

env.VERSION = (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_BUILD_VERSION) || '0';
env.MOCKED = typeof process !== 'undefined' && !!process.env?.NEXT_PUBLIC_MOCKED;

export const BE_URL = env?.TF_SVCGATEWAY_URL || '';
export const GQL_URL = env?.GQL_URL || 'http://localhost:8080/';
export const SHOWCASE_URL =
  env.ENV === 'prod' ? 'https://showcase.teach.ef.studio' : 'https://showcase.teach.dev.ef.studio';

// Mocked mode doubles as demo mode for some customers (the deployed showcase build ships with
// NEXT_PUBLIC_MOCKED=true), so env.MOCKED alone cannot gate developer-only tooling. A developer
// runs mocked mode on localhost; demo customers run it on a deployed host. Gate dev-only UI on
// this instead of env.MOCKED.
export const isDeveloperMode = (): boolean =>
  Boolean(env.MOCKED) && typeof window !== 'undefined' && window.location.hostname === 'localhost';

export default env;
